import { logAdminAction } from "./auditLog";
import { supabase } from "./supabase";

export type InquiryChannel = "company" | "student" | "partnership" | "general";
export type InquiryStatus = "new" | "read" | "responded" | "archived";

export type InquiryRow = {
  id: string;
  channel: InquiryChannel;
  name: string;
  email: string;
  organization: string | null;
  university: string | null;
  inquiry_type: string | null;
  message: string;
  status: InquiryStatus;
  responded_by: string | null;
  responded_at: string | null;
  response_notes: string | null;
  created_at: string;
};

export type InquiriesStats = {
  total: number;
  new_count: number;
  responded_count: number;
  archived_count: number;
  this_week: number;
};

export async function submitInquiry(input: {
  channel: InquiryChannel;
  name: string;
  email: string;
  message: string;
  organization?: string | null;
  university?: string | null;
  inquiry_type?: string | null;
}): Promise<{ error: Error | null }> {
  const name = input.name.trim();
  const email = input.email.trim().toLowerCase();
  const message = input.message.trim();
  const organization = input.organization?.trim() || null;
  const university = input.university?.trim() || null;
  const inquiry_type = input.inquiry_type?.trim() || null;

  const { error } = await supabase.from("company_inquiries").insert({
    channel: input.channel,
    name,
    email,
    message,
    organization,
    university,
    inquiry_type,
  });
  if (error) return { error: new Error(error.message) };

  void import("./analytics").then(({ track }) =>
    track("inquiry_submitted", {
      channel: input.channel,
      inquiry_type: inquiry_type ?? null,
    })
  );

  // Fire-and-forget: route the inquiry to the right Coravu inbox via the
  // notify-admin-inquiry Edge Function and send an auto-reply to the
  // submitter. We don't await this — the row is already in the DB so an
  // admin can respond manually if Resend hiccups, and we'd rather not
  // hold the user on a thank-you screen waiting on a 3rd-party API.
  void supabase.functions
    .invoke("notify-admin-inquiry", {
      body: {
        channel: input.channel,
        name,
        email,
        message,
        organization: organization ?? undefined,
        university: university ?? undefined,
        inquiry_type: inquiry_type ?? undefined,
      },
    })
    .catch((e: unknown) => {
      console.warn("[inquiries] notify-admin-inquiry invoke failed:", e);
    });

  return { error: null };
}

export async function listInquiries(filter?: {
  status?: InquiryStatus;
  channel?: InquiryChannel;
}): Promise<InquiryRow[]> {
  let q = supabase.from("company_inquiries").select("*").order("created_at", { ascending: false });
  if (filter?.status) q = q.eq("status", filter.status);
  if (filter?.channel) q = q.eq("channel", filter.channel);
  const { data, error } = await q;
  if (error || !data) return [];
  return data as InquiryRow[];
}

export async function getInquiriesStats(): Promise<InquiriesStats> {
  const { data, error } = await supabase.rpc("admin_inquiries_stats");
  if (error || data == null) {
    return { total: 0, new_count: 0, responded_count: 0, archived_count: 0, this_week: 0 };
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row || typeof row !== "object") {
    return { total: 0, new_count: 0, responded_count: 0, archived_count: 0, this_week: 0 };
  }
  const r = row as Record<string, unknown>;
  return {
    total: Number(r.total ?? 0),
    new_count: Number(r.new_count ?? 0),
    responded_count: Number(r.responded_count ?? 0),
    archived_count: Number(r.archived_count ?? 0),
    this_week: Number(r.this_week ?? 0),
  };
}

export async function setInquiryStatus(
  id: string,
  status: InquiryStatus,
  response_notes?: string | null
): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const patch: Record<string, unknown> = { status };
  if (status === "responded") {
    patch.responded_at = new Date().toISOString();
    patch.responded_by = userData.user?.id ?? null;
    if (response_notes !== undefined) {
      patch.response_notes = response_notes;
    }
  }
  const { error } = await supabase.from("company_inquiries").update(patch).eq("id", id);
  if (error) throw new Error(error.message);
  // Two distinct actions so the audit log can filter "who responded to
  // what" separately from the more frequent read/archived status flips.
  if (status === "responded") {
    await logAdminAction({
      action: "inquiry.respond",
      entityType: "company_inquiries",
      entityId: id,
      summary: `Marked inquiry responded${response_notes ? `: ${response_notes.slice(0, 80)}` : ""}`,
      diff: { after: { status, response_notes: response_notes ?? null } },
    });
  } else {
    await logAdminAction({
      action: "inquiry.status_set",
      entityType: "company_inquiries",
      entityId: id,
      summary: `Set inquiry status to ${status}`,
      diff: { after: { status } },
    });
  }
}
